import React, { useEffect, useState } from "react"

import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"

import { LoginStep } from "../../../../config/auth.config"
import { ROUTES_CONFIG } from "../../../../config/routes.config"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { authSelector } from "../../../../store/selectors/auth.selector"
import {
    authSetAutoSubmitLoginPasswordStep,
    authSetLoginStep,
} from "../../../../store/slices/auth.slice"
import { authLogin } from "../../../../store/thunks/auth.thunks"
import type { ILoginData } from "../../../../types/data.types"

import arrowLeftIcon from "@front-shared/assets/images/icons/arrow-left-dark2.svg"
import arrowRightIcon from "@front-shared/assets/images/icons/arrow-right2.svg"
import mailIcon from "@front-shared/assets/images/icons/mail-dark.svg"
import mainImage from "@front-shared/assets/images/shapes/login.svg"

export const PasswordStep: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const authState = useAppSelector(authSelector)

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useForm<ILoginData>({
        defaultValues: {
            email: authState.loginEmail || authState.loginData?.email || "",
            password: "",
        },
    })

    const [isLoginError, setIsLoginError] = useState<boolean>(false)
    const [isAutoLoginActive, setIsAutoLoginActive] = useState<boolean>(false)

    // If need to auto submit login form and we have needed data
    useEffect(() => {
        if (!authState.autoSubmitLoginPasswordStep || !authState.loginData) {
            return
        }

        setIsAutoLoginActive(true)

        performLogin(authState.loginData, true)

        return () => {
            dispatch(authSetAutoSubmitLoginPasswordStep(false))
        }
    }, [])

    const performLogin = (data: ILoginData, isAutoLogin = false) => {
        setIsLoginError(false)

        dispatch(
            authLogin({
                payload: data,
                navigate,
                onError: () => {
                    setIsLoginError(true)
                },
                ...(isAutoLogin
                    ? {
                          onFinally: () => {
                              setIsAutoLoginActive(false)
                          },
                      }
                    : {}),
            }),
        )
    }

    return !isAutoLoginActive ? (
        <div className="relative">
            <img
                src={mainImage}
                alt="Nirvana"
                title="Nirvana"
                className="margin-x-center sm:w-[150px]"
            />

            <PrimaryText className="mt-[32px]" typography="h1" centered>
                Welcome back to <br />
                Nirvana
            </PrimaryText>

            <PrimaryText typography="subtitle" centered className="mt-[32px]">
                Insurance coverage solutions optimized for your specialty and
                scale.
            </PrimaryText>

            <form
                onSubmit={handleSubmit(data => {
                    performLogin(data)
                })}
                noValidate={true}
            >
                <div className="mt-[44px] flex items-start md:block">
                    <InputElement
                        className="mr-[24px] flex-1 md:mr-[0px]"
                        name="email"
                        label="Email address"
                        reactHookControl={control as any}
                        reactHookErrors={errors}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            pattern: FRONT_VALIDATION_CONFIG.email,
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        inputSuffix={
                            <img
                                src={mailIcon}
                                alt="Email address"
                                title="Email address"
                                className="ml-[8px]"
                            />
                        }
                        innerInputClassName={
                            isLoginError ? "errored" : undefined
                        }
                        disabled={true}
                        isLabelStatic={true}
                    />

                    <InputElement
                        className="flex-1 md:mt-[44px]"
                        name="password"
                        label="Password"
                        type="password"
                        reactHookControl={control as any}
                        reactHookErrors={errors}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            minLength:
                                FRONT_VALIDATION_CONFIG.minLengthPassword,
                            maxLength:
                                FRONT_VALIDATION_CONFIG.maxLengthPassword,
                        }}
                        innerInputClassName={
                            isLoginError ? "errored" : undefined
                        }
                        autoFocus={true}
                    />
                </div>

                <div className="mt-[32px] flex items-center">
                    <div className="flex-1 mr-[16px]">
                        <Link to={ROUTES_CONFIG.restoreUrl}>
                            Forgot password?
                        </Link>
                    </div>

                    {isLoginError ? (
                        <div className="mr-[24px]">
                            <PrimaryText color={NIRVANA_COLORS.brand.error}>
                                Hmmm… wrong credentials provided. <br />
                                Please try again.
                            </PrimaryText>
                        </div>
                    ) : null}

                    <ButtonElement
                        label="Back to email"
                        type="default"
                        onClick={() => {
                            dispatch(authSetLoginStep(LoginStep.EMAIL))
                        }}
                        size="large"
                        icon={arrowLeftIcon}
                        disabled={authState.isLoading}
                        className="mr-[16px]"
                    />

                    <ButtonElement
                        label="Log in"
                        type="primary"
                        htmlType="submit"
                        size="large"
                        icon={arrowRightIcon}
                        isRightIcon
                        isLoading={authState.isLoading}
                    />
                </div>
            </form>
        </div>
    ) : (
        <SpinnerElement containerClassName="mt-[60px] text-center" />
    )
}
